import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowDown, FileText, FileWarning, Inbox, ListChecks, Mail, Menu, PanelRightOpen, SquarePen, Sun } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageCrumb } from "@/components/drift/DriftLayout";
import { Mark } from "@/components/public/Logo";
import { ConversationList } from "@/components/drift/ai/ConversationList";
import { AssistantMessage, UserMessage } from "@/components/drift/ai/MessageThread";
import { Composer } from "@/components/drift/ai/Composer";
import { CanvasPanel } from "@/components/drift/ai/CanvasPanel";
import { streamMessage } from "@/components/drift/ai/stream";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   /drift/ai – the assistant workspace. Conversations on the left, the
   thread in the middle, the canvas (documents) on the right. Streams
   over SSE; the composer creates a conversation on first send.
   ------------------------------------------------------------------ */

const SUGG_ICON = { sun: Sun, mail: Mail, "file-warning": FileWarning, inbox: Inbox, "file-text": FileText, "list-checks": ListChecks };
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

function useMedia(query) {
  const get = () => (typeof window !== "undefined" ? window.matchMedia(query).matches : true);
  const [m, setM] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const fn = () => setM(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, [query]);
  return m;
}

export default function AiWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const desktop = useMedia("(min-width: 1024px)");

  const [convs, setConvs] = useState(null);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConv, setLoadingConv] = useState(false);
  const [live, setLive] = useState(null);
  const [draft, setDraft] = useState("");
  const [context, setContext] = useState(null);
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [model, setModel] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [atBottom, setAtBottom] = useState(true);

  const abortRef = useRef(null);
  const loadedRef = useRef(null);
  const scrollRef = useRef(null);
  const stickRef = useRef(true);
  const autoRef = useRef(false);
  const liveRef = useRef(null);
  liveRef.current = live;

  usePageCrumb(id ? conv?.title || "Samtale" : null);

  /* ---------- data ---------- */
  useEffect(() => {
    api.get("/drift/ai/conversations").then((r) => setConvs(r.data.items || [])).catch(() => setConvs([]));
    api.get("/drift/ai/suggestions").then((r) => setSuggestions(r.data.items || [])).catch(() => {});
    api.get("/drift/ai/meta").then((r) => setModel(r.data.model || "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) {
      loadedRef.current = null;
      setConv(null); setMessages([]); setCanvasOpen(false); setViewing(null); setLive(null);
      return;
    }
    if (loadedRef.current === id) return;
    loadedRef.current = id;
    setLoadingConv(true);
    setViewing(null);
    api.get(`/drift/ai/conversations/${id}`).then((r) => {
      const { messages: msgs, ...c } = r.data;
      setConv(c); setMessages(msgs || []); setCanvasOpen(!!c.canvas);
      stickRef.current = true;
    }).catch((e) => {
      toast.error(errMsg(e, "Fann ikkje samtalen"));
      navigate("/drift/ai", { replace: true });
    }).finally(() => setLoadingConv(false));
  }, [id, navigate]);

  // Context passed in the URL (e.g. from an order page): ?ctx=order:<id>
  useEffect(() => {
    const ctx = params.get("ctx");
    if (!ctx || context) return;
    const [type, cid] = ctx.split(":");
    const get = type === "order" ? api.get(`/drift/orders/${cid}`).then((r) => ({ type, id: cid, label: `${r.data.ref} · ${r.data.title}`, link: `/drift/oppdrag/${cid}` }))
      : type === "customer" ? api.get(`/drift/customers/${cid}`).then((r) => ({ type, id: cid, label: r.data.name, link: `/drift/kundar/${cid}` }))
      : type === "inquiry" ? api.get(`/drift/inquiries/${cid}`).then((r) => ({ type, id: cid, label: `${r.data.ref} · ${r.data.company || r.data.name}`, link: `/drift/henvendingar/${cid}` }))
      : null;
    get?.then(setContext).catch(() => {});
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- scrolling ---------- */
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 90;
    stickRef.current = near;
    setAtBottom(near);
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, live?.text, live?.steps?.length, loadingConv]);
  const scrollDown = () => { const el = scrollRef.current; if (el) { el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); stickRef.current = true; } };

  /* ---------- conversations ---------- */
  const patchConvInList = useCallback((cid, patch) => setConvs((prev) => (prev || []).map((c) => (c.id === cid ? { ...c, ...patch } : c))), []);
  const newConversation = useCallback(() => { abortRef.current?.abort(); setDraft(""); setContext(null); navigate("/drift/ai"); }, [navigate]);
  const selectConversation = (cid) => { setListOpen(false); if (cid !== id) { abortRef.current?.abort(); navigate(`/drift/ai/${cid}`); } };
  const renameConversation = (cid, title) => {
    patchConvInList(cid, { title });
    if (cid === id) setConv((c) => (c ? { ...c, title } : c));
    api.patch(`/drift/ai/conversations/${cid}`, { title }).catch((e) => toast.error(errMsg(e, "Fekk ikkje endra namnet")));
  };
  const pinConversation = (cid, pinned) => {
    patchConvInList(cid, { pinned });
    api.patch(`/drift/ai/conversations/${cid}`, { pinned }).catch((e) => toast.error(errMsg(e)));
  };
  const deleteConversation = (cid) => {
    setConvs((prev) => (prev || []).filter((c) => c.id !== cid));
    api.delete(`/drift/ai/conversations/${cid}`).then(() => toast.success("Samtalen er sletta")).catch((e) => toast.error(errMsg(e, "Fekk ikkje sletta")));
    if (cid === id) navigate("/drift/ai");
  };

  /* ---------- send / stream ---------- */
  const send = useCallback(async (textArg, ctxArg) => {
    const text = (textArg ?? draft).trim();
    const ctx = ctxArg === undefined ? context : ctxArg;
    if (!text || liveRef.current) return;
    setDraft(""); setContext(null);
    let cid = conv?.id;
    if (!cid) {
      try {
        const r = await api.post("/drift/ai/conversations", {});
        const created = r.data;
        cid = created.id;
        loadedRef.current = cid;
        setConv({ ...created, versions: [] });
        setConvs((prev) => [created, ...(prev || [])]);
        navigate(`/drift/ai/${cid}`, { replace: true });
      } catch (e) {
        toast.error(errMsg(e, "Fekk ikkje starta samtalen"));
        setDraft(text);
        return;
      }
    }
    const tmpId = `tmp-${Date.now()}`;
    setMessages((m) => [...m, { id: tmpId, role: "user", content: text, context: ctx ? { label: ctx.label, link: ctx.link } : null, created_at: new Date().toISOString() }]);
    setLive({ id: null, text: "", steps: [], status: "thinking" });
    stickRef.current = true;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const handle = (ev) => {
      switch (ev.type) {
        case "start":
          setMessages((m) => m.map((x) => (x.id === tmpId ? ev.user_message : x)));
          setLive((l) => (l ? { ...l, id: ev.assistant_id } : l));
          break;
        case "delta":
          setLive((l) => (l ? { ...l, text: l.text + ev.text, status: "streaming" } : l));
          break;
        case "tool":
          setLive((l) => {
            if (!l) return l;
            const exists = l.steps.some((s) => s.id === ev.id);
            const step = { id: ev.id, name: ev.name, label: ev.label, phase: ev.phase, summary: ev.summary, data: ev.data };
            return { ...l, steps: exists ? l.steps.map((s) => (s.id === ev.id ? { ...s, ...step } : s)) : [...l.steps, step] };
          });
          break;
        case "canvas":
          setConv((c) => (c ? { ...c, canvas: ev.canvas, has_canvas: true, versions: [...(c.versions || []).filter((v) => v.version !== ev.canvas.version), { ...ev.canvas, content: undefined }] } : c));
          setViewing(null);
          setCanvasOpen(true);
          patchConvInList(cid, { has_canvas: true });
          break;
        case "done":
          setMessages((m) => [...m, ev.message]);
          setLive(null);
          setConvs((prev) => (prev || []).map((c) => (c.id === cid ? { ...c, updated_at: new Date().toISOString(), preview: text.slice(0, 120), message_count: (c.message_count || 0) + 2 } : c)));
          break;
        case "title":
          setConv((c) => (c ? { ...c, title: ev.title } : c));
          patchConvInList(cid, { title: ev.title });
          break;
        case "error":
          setLive(null);
          setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "assistant", content: "", error: ev.message, created_at: new Date().toISOString() }]);
          break;
        default:
          break;
      }
    };
    try {
      await streamMessage({ convId: cid, content: text, context: ctx, signal: ctrl.signal, onEvent: handle });
    } catch (e) {
      if (e.name === "AbortError") return;
      setLive(null);
      setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "assistant", content: "", error: e.message || "Assistenten svarte ikkje.", created_at: new Date().toISOString() }]);
    } finally {
      if (abortRef.current === ctrl) abortRef.current = null;
      setLive(null);
    }
  }, [draft, context, conv?.id, navigate, patchConvInList]);

  const stop = () => {
    const l = liveRef.current;
    abortRef.current?.abort();
    abortRef.current = null;
    if (l) {
      setMessages((m) => [...m, { id: l.id || `stop-${Date.now()}`, role: "assistant", content: l.text || "Stoppa.", steps: l.steps.filter((s) => s.phase === "done"), partial: true, created_at: new Date().toISOString() }]);
    }
    setLive(null);
  };

  // ?ask=… from the command menu: send once.
  useEffect(() => {
    const ask = params.get("ask");
    if (!ask || autoRef.current || id) return;
    autoRef.current = true;
    send(ask, null);
  }, [params, id, send]);

  // ⌘⇧O → new conversation
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "o") { e.preventDefault(); newConversation(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [newConversation]);

  useEffect(() => () => abortRef.current?.abort(), []);

  /* ---------- canvas ---------- */
  const loadVersion = (n) => {
    if (!conv) return;
    if (n === conv.canvas?.version) { setViewing(null); return; }
    api.get(`/drift/ai/conversations/${conv.id}/versions/${n}`).then((r) => setViewing(r.data)).catch((e) => toast.error(errMsg(e, "Fann ikkje versjonen")));
  };
  const saveCanvas = (content, title) => {
    api.patch(`/drift/ai/conversations/${conv.id}/canvas`, { content, title }).then((r) => {
      setConv((c) => ({ ...c, canvas: r.data, versions: [...(c.versions || []), { ...r.data, content: undefined }] }));
      setViewing(null);
      toast.success("Dokumentet er lagra");
    }).catch((e) => toast.error(errMsg(e, "Fekk ikkje lagra")));
  };
  const restoreVersion = (v) => saveCanvas(v.content, v.title);
  const openCanvas = (data) => {
    if (data?.version && conv?.canvas && data.version !== conv.canvas.version) loadVersion(data.version);
    else setViewing(null);
    setCanvasOpen(true);
  };
  const askCanvas = (prompt) => send(prompt, null);

  /* ---------- render ---------- */
  const first = (user?.name || "").split(" ")[0];
  const empty = !id && messages.length === 0 && !live;
  const liveMsg = live ? { id: live.id, role: "assistant", content: live.text, steps: live.steps } : null;
  const showCanvas = canvasOpen && !!conv?.canvas;

  const thread = (
    <div className="mx-auto w-full max-w-[760px] px-4 pb-6 pt-5 sm:px-6" data-testid="ai-thread">
      {loadingConv && (
        <div className="space-y-6" aria-busy="true" data-testid="ai-thread-loading">
          <div className="flex justify-end"><Skeleton className="h-10 w-2/3 rounded-2xl bg-secondary" /></div>
          <div className="flex gap-3.5"><Skeleton className="h-6 w-6 rounded-md bg-secondary" /><div className="flex-1 space-y-2"><Skeleton className="h-4 w-11/12 rounded-md bg-secondary" /><Skeleton className="h-4 w-4/5 rounded-md bg-secondary" /><Skeleton className="h-4 w-1/2 rounded-md bg-secondary" /></div></div>
        </div>
      )}
      {!loadingConv && (
        <div className="space-y-7">
          {messages.map((m) => (m.role === "user" ? <UserMessage key={m.id} m={m} /> : <AssistantMessage key={m.id} m={m} onOpenCanvas={openCanvas} />))}
          {liveMsg && <AssistantMessage m={liveMsg} live onOpenCanvas={openCanvas} />}
        </div>
      )}
    </div>
  );

  const welcome = (
    <div className="mx-auto flex w-full max-w-[760px] flex-col px-4 pb-8 pt-10 sm:px-6 lg:pt-[9vh]" data-testid="ai-welcome">
      <Mark className="h-9 w-9" title="" aria-hidden="true" />
      <h1 className="mt-5 font-display text-3xl font-medium tracking-[-0.025em] sm:text-[2.35rem]" data-testid="ai-welcome-title">Kva kan eg hjelpe deg med{first ? `, ${first}` : ""}?</h1>
      <p className="mt-2 max-w-[56ch] text-[14.5px] leading-relaxed text-muted-foreground">Spør om oppdrag, kundar, henvendingar og dagsplan – eller be om eit utkast til e-post, tilbodstekst, rapport eller sjekkliste. Dokument hamnar i canvas ved sida av, klare til å redigerast.</p>
      <Composer className="mt-7" value={draft} onChange={setDraft} onSend={() => send()} onStop={stop} streaming={!!live} context={context} onContext={setContext} model={model} />
      <p className="mt-2 hidden px-1 text-[11.5px] text-muted-foreground sm:block">Enter sender · Shift+Enter gir linjeskift · <span className="kbd">{isMac ? "⌘" : "Ctrl"}K</span> søkjer i Drift</p>
      {suggestions.length > 0 && (
        <div className="mt-8" data-testid="ai-suggestions">
          <p className="mb-2.5 text-[12px] font-medium text-muted-foreground">Forslag basert på det som skjer i Drift no</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {suggestions.map((s) => {
              const Icon = SUGG_ICON[s.icon] || FileText;
              return (
                <button key={s.label} type="button" onClick={() => send(s.prompt, null)} className="group flex items-start gap-3 rounded-xl bg-card p-3.5 text-left shadow-panel transition-[background-color,transform] duration-200 ease-premium hover:-translate-y-px hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="ai-suggestion">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-brand" strokeWidth={1.75} aria-hidden />
                  <span className="min-w-0">
                    <span className="block text-[13.5px] font-medium leading-snug">{s.label}</span>
                    <span className="mt-1 line-clamp-2 block text-[12px] leading-snug text-muted-foreground">{s.prompt}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const chatPane = (
    <div className="flex h-full min-h-0 flex-col" data-testid="ai-chat-pane">
      {/* Pane header */}
      <div className="flex h-11 shrink-0 items-center justify-between gap-2 px-2 sm:px-3 lg:px-5">
        <div className="flex min-w-0 items-center gap-1">
          <button type="button" onClick={() => setListOpen(true)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent/60 hover:text-foreground lg:hidden" aria-label="Samtalar" data-testid="ai-open-list-button"><Menu className="h-4 w-4" strokeWidth={1.75} /></button>
          <button type="button" onClick={newConversation} className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent/60 hover:text-foreground lg:hidden" aria-label="Ny samtale" data-testid="ai-new-conversation-button-mobile"><SquarePen className="h-4 w-4" strokeWidth={1.75} /></button>
          <p className="truncate px-1 text-[13px] font-medium text-foreground/90" data-testid="ai-pane-title">{id ? conv?.title || "" : "Ny samtale"}</p>
        </div>
        <div className="flex items-center gap-1">
          {model && <span className="hidden font-mono text-[11px] text-muted-foreground lg:inline">{model}</span>}
          {conv?.canvas && !showCanvas && (
            <button type="button" onClick={() => openCanvas()} className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-[12.5px] font-medium text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="ai-open-canvas-button">
              <PanelRightOpen className="h-4 w-4" strokeWidth={1.75} aria-hidden /> <span className="hidden sm:inline">Vis dokument</span>
            </button>
          )}
        </div>
      </div>

      {/* Thread */}
      <div ref={scrollRef} onScroll={onScroll} className="relative min-h-0 flex-1 overflow-y-auto scrollbar-thin" data-testid="ai-scroll">
        {empty ? welcome : thread}
      </div>

      {/* Composer */}
      {!empty && (
        <div className="relative shrink-0 px-3 pb-3 pt-1 sm:px-6 sm:pb-4">
          {!atBottom && (
            <button type="button" onClick={scrollDown} className="shadow-pop absolute -top-10 left-1/2 inline-flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-card text-muted-foreground hover:text-foreground" aria-label="Til botnen" data-testid="ai-scroll-down"><ArrowDown className="h-4 w-4" strokeWidth={1.75} /></button>
          )}
          <div className="mx-auto w-full max-w-[760px]">
            <Composer compact value={draft} onChange={setDraft} onSend={() => send()} onStop={stop} streaming={!!live} context={context} onContext={setContext} autoFocus={false} />
            <p className="mt-1.5 hidden text-center text-[11px] text-muted-foreground/80 sm:block">Assistenten kan ta feil. Sjekk fakta mot Drift før du sender noko vidare.</p>
          </div>
        </div>
      )}
    </div>
  );

  const canvasEl = showCanvas ? (
    <CanvasPanel canvas={conv.canvas} versions={conv.versions} viewing={viewing} onLoadVersion={loadVersion} onRestore={restoreVersion} onSave={saveCanvas} onClose={() => { setCanvasOpen(false); setViewing(null); }} onAsk={askCanvas} streaming={!!live} />
  ) : null;

  return (
    <div className="flex h-full min-h-0" data-testid="ai-workspace">
      {/* Conversations (desktop) */}
      <aside className="hidden w-[256px] shrink-0 border-r border-border/70 bg-background/50 lg:flex lg:flex-col" data-testid="ai-sidebar">
        <ConversationList items={convs} activeId={id} onNew={newConversation} onSelect={selectConversation} onRename={renameConversation} onDelete={deleteConversation} onPin={pinConversation} isMac={isMac} />
      </aside>

      {/* Chat + canvas */}
      {desktop ? (
        <ResizablePanelGroup direction="horizontal" className="min-w-0 flex-1" autoSaveId="ke-ai-panels">
          <ResizablePanel id="chat" order={1} defaultSize={showCanvas ? 54 : 100} minSize={36} className="min-w-0">
            {chatPane}
          </ResizablePanel>
          {showCanvas && (
            <>
              <ResizableHandle className="w-px bg-border/70 transition-colors after:w-1.5 hover:bg-brand data-[resize-handle-active]:bg-brand" />
              <ResizablePanel id="canvas" order={2} defaultSize={46} minSize={30} className="min-w-0">
                {canvasEl}
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      ) : (
        <div className="min-w-0 flex-1">{chatPane}</div>
      )}

      {/* Mobile: conversations and canvas as sheets */}
      <Sheet open={listOpen} onOpenChange={setListOpen}>
        <SheetContent side="left" className="w-[300px] bg-background p-0" data-testid="ai-list-sheet">
          <SheetTitle className="sr-only">Samtalar</SheetTitle>
          <ConversationList items={convs} activeId={id} onNew={() => { setListOpen(false); newConversation(); }} onSelect={selectConversation} onRename={renameConversation} onDelete={deleteConversation} onPin={pinConversation} isMac={isMac} className="pt-9" />
        </SheetContent>
      </Sheet>
      {!desktop && (
        <Sheet open={showCanvas} onOpenChange={(o) => { if (!o) { setCanvasOpen(false); setViewing(null); } }}>
          <SheetContent side="bottom" className="h-[92vh] rounded-t-2xl bg-card p-0 [&>button]:hidden" data-testid="ai-canvas-sheet">
            <SheetTitle className="sr-only">Dokument</SheetTitle>
            <div className="h-full pt-2">{canvasEl}</div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
